import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { apiConfig } from '../aws-config';

interface Queue {
  queueId: string;
  queueName: string;
}

interface QueueSelectionPageProps {
  signOut?: () => void;
}

const QueueSelectionPage: React.FC<QueueSelectionPageProps> = ({ signOut }) => {
  const { theme, isDark, toggleTheme } = useTheme();
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueues, setSelectedQueues] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [personalLink, setPersonalLink] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const response = await fetch(`${apiConfig.baseUrl}/queues`);
        const queueData = await response.json();
        setQueues(queueData);
      } catch (error) {
        console.error('Error fetching queues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQueues();
  }, []);

  const handleQueueToggle = (queueId: string) => {
    setSelectedQueues(prev => 
      prev.includes(queueId) 
        ? prev.filter(id => id !== queueId)
        : [...prev, queueId]
    );
  };

  const handleGeneratePersonalLink = async () => {
    if (selectedQueues.length === 0) {
      alert('Please select at least one queue');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.createShareLink}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queueIds: selectedQueues })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      if (!result.linkId) {
        throw new Error('No linkId returned from API');
      }
      
      const shareUrl = `${window.location.origin}/shared/${result.linkId}`;
      setPersonalLink(shareUrl);
    } catch (error) {
      console.error('Error creating personal link:', error);
      alert('Failed to create personal link');
    } finally {
      setCreating(false);
    }
  };

  const copyPersonalLink = () => {
    navigator.clipboard.writeText(personalLink);
    alert('Personal link copied to clipboard!');
  };

  const handleGenerateDashboard = async () => {
    if (selectedQueues.length === 0) {
      alert('Please select at least one queue');
      return;
    }

    setCreating(true);
    try {
      // Always go to multi-queue page with comma-separated IDs
      navigate(`/multi-queue/${selectedQueues.join(',')}`);
    } catch (error) {
      console.error('Error generating dashboard:', error);
      alert('Failed to generate dashboard');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading queues...</div>;
  }

  if (!queues || queues.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <h1>No Queues Found</h1>
        <p>No queues available to monitor.</p>
        <button onClick={signOut}>Sign Out</button>
      </div>
    );
  }

  // Show all queues if search is empty, else show filtered matches only
  const queuesToShow = searchText.trim() === ''
    ? queues
    : queues.filter(q => q.queueName.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', backgroundColor: theme.background, color: theme.text }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img src={isDark ? "/Newcastle-City-Council-3-1.webp" : "/ClientLogo-NewcastleCityCouncil.png"} alt="Company Logo" style={{ width: '250px', height: 'auto' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Select Queues to Monitor</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={toggleTheme} style={{
            padding: '8px 16px',
            backgroundColor: theme.surface,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer'
          }}>
            {isDark ? '☀️ Light' : '🌙 Dark'}
          </button>
          <button 
            onClick={signOut}
            style={{
              padding: '10px 20px',
              backgroundColor: theme.danger,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search queues..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '16px',
            borderRadius: '6px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.surface,
            color: theme.text
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <p>Choose one or more queues to monitor:</p>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '10px',
        marginBottom: '30px'
      }}>
        {queuesToShow.length > 0 ? queuesToShow
          .sort((a, b) => (a.queueName || '').localeCompare(b.queueName || ''))
          .map(queue => (
          <label 
            key={queue.queueId}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '15px',
              border: selectedQueues.includes(queue.queueId) ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: selectedQueues.includes(queue.queueId) ? theme.sidebar : theme.surface,
              transition: 'all 0.2s ease'
            }}
          >
            <input
              type="checkbox"
              checked={selectedQueues.includes(queue.queueId)}
              onChange={() => handleQueueToggle(queue.queueId)}
              style={{ marginRight: '10px', transform: 'scale(1.2)' }}
            />
            <span style={{ 
              fontWeight: selectedQueues.includes(queue.queueId) ? 'bold' : 'normal',
              color: theme.text
            }}>
              {queue.queueName || 'Unknown Queue'}
            </span>
          </label>
        )) : (
          <div>No queues found matching "{searchText}"</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleGenerateDashboard}
          disabled={creating || selectedQueues.length === 0}
          style={{
            padding: '12px 24px',
            backgroundColor: selectedQueues.length > 0 ? theme.primary : theme.textSecondary,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedQueues.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'background-color 0.2s ease'
          }}
        >
          {creating ? 'Generating...' : `Generate Dashboard (${selectedQueues.length} selected)`}
        </button>

        <button
          onClick={handleGeneratePersonalLink}
          disabled={creating || selectedQueues.length === 0}
          style={{
            padding: '12px 24px',
            backgroundColor: selectedQueues.length > 0 ? '#10b981' : theme.textSecondary,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedQueues.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'background-color 0.2s ease'
          }}
        >
          {creating ? 'Creating...' : 'Generate Personal Link'}
        </button>
      </div>

      {personalLink && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: theme.surface, borderRadius: '6px', border: `1px solid ${theme.border}` }}>
          <h3>Personal Dashboard Link (30-day expiry):</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
            <input 
              type="text" 
              value={personalLink} 
              readOnly 
              style={{ 
                flex: 1, 
                padding: '8px', 
                border: `1px solid ${theme.border}`, 
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text
              }}
            />
            <button 
              onClick={copyPersonalLink}
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Copy
            </button>
          </div>
          <p style={{ fontSize: '14px', color: theme.textSecondary, marginTop: '10px' }}>
            Share this link with others or bookmark it for quick access to your selected queues.
          </p>
        </div>
      )}

      {selectedQueues.length > 0 && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: theme.surface, borderRadius: '6px', border: `1px solid ${theme.border}` }}>
          <h3>Selected Queues ({selectedQueues.length}):</h3>
          <ul>
            {selectedQueues.map(queueId => {
              const queue = queues.find(q => q.queueId === queueId);
              return <li key={queueId}>{queue?.queueName || queueId}</li>;
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default QueueSelectionPage;